/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import { VStack } from '../../core-ui';
import { canUser } from '../../support/shared/permissionHelpers';
import Imm from '../../support/ImmutableHelpers';
import { postJSON } from '../../support/Fetch';
import {
  formatDateTime,
  formatDateFullWithHours,
} from '../../support/DateTime';
import { APP_FIELDS } from '../../support/shared/constants/Constants';
import { AppStatus } from '../../core/constants/AppStatus';
import getShortVendorId from '../../support/shared/getShortVendorId';
import VendorActions from '../../support/VendorActions';
import { Tag } from '../../types/shared/Tag';
import { User } from '../../types/shared/User';
import { showSuccess, showError } from '../App/AppAlert';

import Preview from '../Preview';
import NamedList from '../uiold/NamedList';

import LoadingIndicator from '../uiold/LoadingIndicator';
import {
  CollapsibleView,
  Card,
  Text,
  TextInput,
  ListItem,
  Button,
  IconWithText,
  Picker,
} from '../uiold';
import { themeColors } from '../uiold/Constants';

import SignupFormModel from '../FormBuilder/SignupFormModel';
import calculatePrice, {
  formatChoiceWithCharge,
  parseChargeFromValue,
} from '../../support/shared/calculatePrice';
import formatCurrency from '../../core/support/formatCurrency';
import InvoiceControl from './InvoiceControl';
import { CurrencyProvider } from '../CurrencyContext';
import IndexedList from '../../support/shared/IndexedList';
import { FileDesc } from '../../types/shared/FileDesc';
import { Field, Vendor } from '../../types/shared/Vendor';
import { Market } from '../../types/shared/Market';
import { Tier } from '../../types/shared/Tier';
import { Account } from '../../types/shared/Account';
import { Addon } from '../../types/shared/Addon';
import { TagLabelView } from '../Vendors/VendorTag';

import TagPicker from './PickerOld';
import { confirm } from '../App/AppDialog';
import { MarketsView } from '../FormFields/MarketsView';
import { MarketsEditView } from '../FormFields/MarketsEditView';
import { MultichoiceElementDefinitionRuntype } from '../../runtypes/SignupFormDefinitionRuntype';
import { getOptionFields } from '../../support/helpers/CategoryOptions';
import { getAllPaymentLineItems } from '../../support/shared/billing';

type AddOrRemove = 'ADD' | 'REMOVE';

type Props = {
  vendorID: string;
  account: Account;
  user: User | null | undefined;
  vendor: Vendor;
  addons: Map<string, Addon>;
  markets: IndexedList<Market>;
  formDef: SignupFormModel;
  priceTiers: IndexedList<Tier>;
  tags: Array<Tag>;
  onHide: () => void;
  saveVendor: (vendor: Vendor) => void;
  isLoading: boolean;
  isSaving: boolean;
  refetchAll: () => void;
};

type State = {
  editingVendor: Vendor | null | undefined;
  previewAttachment: FileDesc | null | undefined;
  statusChangePending: string | null | undefined;
  statusChangeSuccessful: boolean;
};

export class VendorDetailsView extends Component<Props, State> {
  _isMounted = true;
  _statusSuccessIndicatorTimeout: TimeoutID;

  state: State = {
    editingVendor: null,
    previewAttachment: null,
    statusChangePending: null,
    statusChangeSuccessful: false,
  };

  componentDidMount() {
    document.addEventListener('keydown', this._onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this._onKeyDown);
    clearTimeout(this._statusSuccessIndicatorTimeout);
    this._isMounted = false;
  }

  render() {
    let { account, isLoading, isSaving, vendor, user } = this.props;

    if (isLoading || isSaving || !vendor) {
      return <LoadingIndicator />;
    }
    return (
      <CurrencyProvider
        value={{
          currency: (account.settings && account.settings.currency) || 'USD',
        }}
      >
        <TouchableOpacity onPress={this._backButton} style={styles.back}>
          <IconWithText
            iconName="arrowBack"
            text={t('Back to list')}
            color={themeColors.mainNavText}
          />
        </TouchableOpacity>
        <View style={styles.root}>
          <Card style={styles.leftCard}>
            <View style={styles.buttonHeader}>
              {canUser(user, { vendors: 'WRITE' }) && this._renderButtons()}
            </View>
            <View style={styles.leftItem}>
              {this.state.editingVendor
                ? this._renderEditor()
                : this._renderFields()}
              {this._renderPreview()}
            </View>
          </Card>
          <Card style={styles.rightCard}>{this._renderInvoiceField()}</Card>
        </View>
      </CurrencyProvider>
    );
  }

  _renderButtons = (): ReactElement => {
    if (this.state.editingVendor) {
      return (
        <View style={styles.buttonContainer}>
          <Button
            onPress={this._onCancel}
            title={t('Cancel')}
            style={styles.button}
          />
          <Button
            testID="button-vendor-details-save"
            isPrimary
            onPress={this._onSave}
            title={t('Save')}
            style={styles.button}
          />
        </View>
      );
    } else {
      return (
        <View style={styles.buttonContainer}>
          <Button
            testID="button-vendor-details-edit"
            onPress={this._onEditClick}
            title={t('Edit')}
            style={styles.button}
          />
        </View>
      );
    }
  };

  _backButton = () => {
    if (!this.state.editingVendor) {
      this.props.onHide();
    } else {
      this._onCancel();
    }
  };

  _renderFields = () => {
    let { formDef, vendor, account, markets } = this.props;
    let currency = account.settings && account.settings.currency;
    if (!vendor) {
      return;
    }
    let items = [];
    for (let fieldDef of formDef.fields.toArray()) {
      let field = vendor.fields.getByID(fieldDef.id);
      let value = field == null ? null : field.value;
      if (MultichoiceElementDefinitionRuntype.guard(field)) {
        let optionsWithCharge = parseChargeFromValue(field.options);

        value = optionsWithCharge.map(({ name, charge, operator = '+' }) => {
          return formatChoiceWithCharge(name, charge, currency, operator);
        });
      }
      let element =
        fieldDef.id === APP_FIELDS.ID_FIELD_MARKETS ? (
          <MarketsView
            fieldValue={field ?? undefined}
            markets={markets.getList()}
          />
        ) : (
          fieldDef.renderReadOnlyView({
            value,
            isAdmin: true,
            onFileClick: this._onFileClick,
            definition: fieldDef.definition,
            fields: vendor.fields,
          } as any)
        );

      if (element != null) {
        let { definition, id } = fieldDef;
        let name = typeof definition.name === 'string' ? definition.name : '';
        items.push(
          <ListItem style={styles.marginItem} key={`view-${id}`} label={name}>
            {typeof element === 'string' ? (
              <Text style={styles.textLabel}>{element}</Text>
            ) : (
              element
            )}
          </ListItem>,
        );
      }
    }
    return (
      <NamedList className="read-only">
        {items}
        <ListItem style={styles.marginItem} label={t('Price Estimate')}>
          <Text type="label">{this._renderPrice(vendor)}</Text>
        </ListItem>
        {this._renderPaymentTable(vendor)}
        <ListItem style={styles.marginItem} label={t('Internal Notes')}>
          <Text type="label">{vendor.notes}</Text>
        </ListItem>
      </NamedList>
    );
  };

  _renderEditor = () => {
    let formData = this.state.editingVendor;
    let { formDef, vendor, markets, addons } = this.props;
    if (!formData) {
      return;
    }
    let items: Array<React.ReactElement> = [];
    formDef.fields.forEach((fieldDef) => {
      let { id, definition } = fieldDef;
      let isHidden =
        typeof definition.hidden === 'boolean' ? definition.hidden : false;
      if (isHidden || !formData) {
        return;
      }
      let field = formData.fields.getByID(id);
      let value = field?.value ?? null;

      let element =
        fieldDef.id === APP_FIELDS.ID_FIELD_MARKETS ? (
          <MarketsEditView
            fieldValue={field ?? undefined}
            markets={markets.getList()}
            onChange={(field) => this._onFieldChange(fieldDef.id, field)}
          />
        ) : (
          fieldDef.renderEditView({
            value: value ?? null,
            onChange: this._onEdit,
            markets,
            vendor,
            // The following two are used only for AddonElementEditView
            addons,
            fields: formData.fields,
          } as any)
        );
      if (element != null) {
        let name = typeof definition.name === 'string' ? definition.name : '';
        items.push(
          <ListItem style={styles.marginItem} key={`edit-${id}`} label={name}>
            {element}
          </ListItem>,
        );
      }
    });
    return (
      <NamedList className="editing">
        {items}
        <ListItem style={styles.marginItem} label={t('Internal Notes')}>
          <TextInput
            multiline={true}
            numberOfLines={3}
            value={formData.notes}
            onChangeText={this._onEditNotes}
          />
        </ListItem>
      </NamedList>
    );
  };

  _renderPrice = (vendor: Vendor) => {
    let { markets, priceTiers, account, addons, formDef } = this.props;
    let currency = account.settings?.currency ?? 'USD';
    const optionFields = getOptionFields(formDef);
    let { totalPrice } = calculatePrice(
      markets,
      vendor.fields,
      priceTiers.toArray(),
      addons,
      optionFields,
    );
    return formatCurrency(Math.max(0, totalPrice) * 100, currency);
  };

  _renderPaymentTable = (vendor: Vendor) => {
    if (!vendor.billing) {
      return;
    }
    const paymentLineItems = getAllPaymentLineItems(
      vendor.billing.invoices ?? [],
    );
    let dataLines = paymentLineItems.map((pay, i) => (
      <Text type="label" key={i}>
        {formatDateTime(pay.created_at) +
          ' - ' +
          formatCurrency(pay.amount, pay.currency)}
      </Text>
    ));
    return (
      <ListItem
        style={styles.marginItem}
        label={t('Payments')}
        labelStyle={styles.textLabel}
      >
        <CollapsibleView
          noLeftCheckbox
          title={t('Show Payments')}
          titleOnExpand={t('Hide Payments')}
          initialCollapseState={true}
          isChecked={false}
          onChecked={() => {
            return;
          }}
        >
          {dataLines}
        </CollapsibleView>
      </ListItem>
    );
  };

  _renderInvoiceField = () => {
    let {
      account,
      markets,
      vendor,
      priceTiers,
      tags,
      user,
      addons,
      formDef,
    } = this.props;

    if (!vendor) {
      return;
    }
    return (
      <View style={styles.signUp}>
        <View style={styles.marginRightItem}>
          <Text style={styles.textLabel}>Sign up date</Text>
          <Text type="label">{formatDateFullWithHours(vendor.createdAt)}</Text>
        </View>

        {this._renderStatusEditor()}

        <View style={styles.marginRightItem}>
          <Text style={styles.textLabel}>{t('Tags')}</Text>
          {vendor.tags?.length ? null : <Text>{t('(no tags to show)')}</Text>}
          <TagsDisplay vendor={vendor} tags={tags} />
        </View>
        {canUser(user, { vendors: 'WRITE' }) && (
          <>
            <View style={[styles.marginRightItem, { zIndex: 1 }]}>
              <TagPicker
                testID="vendor-details-tag-change"
                options={tags}
                selectedItem={null}
                displayLabel={t('Add/Remove Tags')}
                labelStyle={{ fontSize: 14 }}
                getItemLabel={(tag) => tag.name}
                getItemId={(tag) => tag.id}
                renderIcon={({ color }) => (
                  <View style={[styles.tagItem, { backgroundColor: color }]} />
                )}
                onChange={(tag) => {
                  this._toggleTag(tag);
                }}
              />
            </View>

            <View style={styles.marginRightItem}>
              <Text style={styles.textLabel}>{t('Invoice')}</Text>
              <InvoiceControl
                account={account}
                markets={markets}
                vendor={vendor}
                priceTiers={priceTiers}
                addons={addons}
                formDef={formDef}
              />
            </View>
          </>
        )}
        <View style={styles.marginRightItem}>
          <Text style={styles.textLabel}>{t('Vendor ID')}</Text>
          <Text type="label">{getShortVendorId(vendor.id)}</Text>
        </View>
      </View>
    );
  };

  _renderStatusEditor = () => {
    let { vendor, user } = this.props;
    if (!vendor) {
      return;
    }
    if (!canUser(user, { vendors: 'WRITE' })) {
      return (
        <ListItem label={t('Status')} style={styles.marginRightItem}>
          <Text>{vendor.status}</Text>
        </ListItem>
      );
    }
    let { statusChangePending, statusChangeSuccessful } = this.state;
    if (statusChangePending) {
      return (
        <ListItem style={styles.marginItem} label={t('Status')}>
          <LoadingIndicator />
        </ListItem>
      );
    }

    // TODO: Do something similar to statusMap in VendorStatus.tsx
    const appStatusesMap: { [key: string]: string } = {
      'Pending Review': t('Pending Review'),
      Approved: t('Approved'),
      Active: t('Active'),
      Declined: t('Declined'),
      Waitlisted: t('Waitlisted'),
    };

    return (
      <ListItem
        label={t('Status')}
        style={[styles.marginRightItem, { zIndex: 2 }]}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Picker
            options={Object.keys(appStatusesMap)}
            selectedItem={vendor.status}
            onChange={this._onStatusChange}
            getDisplayName={(item) => {
              return appStatusesMap[item] ?? '';
            }}
          />
          {statusChangeSuccessful ? (
            <IconWithText
              iconName="roundCheck"
              iconSize="S"
              text={t('Saved.')}
              textStyle={styles.savedText}
              iconStyle={styles.savedIcon}
            />
          ) : null}
        </View>
      </ListItem>
    );
  };

  _renderPreview = () => {
    let file = this.state.previewAttachment;
    if (!file) {
      return;
    }
    return (
      <Preview
        file={file}
        onClose={this._closePreview}
        disablePageScroll={true}
      />
    );
  };

  _onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (!this.state.editingVendor && !this.state.previewAttachment) {
        this.props.onHide();
      } else if (!this.state.previewAttachment) {
        this._onCancel();
      }
    }
  };

  _onEdit = (fieldID: string, newValue: unknown) => {
    let { formDef } = this.props;
    let fieldDef = formDef?.fields.getByID(fieldID);
    if (!fieldDef) {
      return;
    }
    let { definition } = fieldDef;
    let name = typeof definition.name === 'string' ? definition.name : '';
    this._onFieldChange(fieldID, {
      id: fieldID,
      name,
      value: newValue ?? null,
    });
  };

  _onFieldChange = (fieldID: string, newField: Field) => {
    let formData = this.state.editingVendor;
    if (!formData) {
      return;
    }
    let fields = formData.fields;
    // If it doesn't exist, should we insert it in the right order?
    let newFields = fields.hasID(fieldID)
      ? fields.setByID(fieldID, newField)
      : fields.push(newField);
    let newFormData = Imm.setProperty(formData, 'fields', newFields);
    this.setState({
      editingVendor: newFormData,
    });
  };

  _onEditNotes = (newValue: string) => {
    let formData = this.state.editingVendor;
    if (!formData) {
      return;
    }
    let newFormData = Imm.setProperty(formData, 'notes', newValue);
    this.setState({
      editingVendor: newFormData,
    });
  };

  _onEditClick = () => {
    this.setState({
      editingVendor: this.props.vendor,
    });
  };

  _onCancel = () => {
    this.setState({ editingVendor: null });
  };

  _onSave = () => {
    let oldVendor = this.props.vendor;
    let newVendor = this.state.editingVendor;
    if (!oldVendor || !newVendor) {
      return;
    }
    // TODO: Why are we excluding status here?
    let { status, ...partialVendor } = newVendor;
    VendorActions.updateVendor(newVendor.id, partialVendor);
    this.props.saveVendor({
      ...partialVendor,
      status: oldVendor.status,
    });
    this.setState({ editingVendor: null });
  };

  _onFileClick = (file: FileDesc, event: Event) => {
    event.preventDefault();
    this.setState({ previewAttachment: file });
  };

  _closePreview = () => {
    this.setState({ previewAttachment: null });
  };

  _onStatusChange = async (newStatus: string) => {
    const { vendor } = this.props;
    if (!vendor) {
      return;
    }

    const oldStatus = vendor.status;
    if (oldStatus === AppStatus.PENDING && newStatus === AppStatus.APPROVED) {
      const vendorDates = vendor.fields.getByID(APP_FIELDS.ID_FIELD_MARKETS);
      if (!vendorDates?.dateSelectionByMarket) {
        return;
      }
      const requestedDatesCount = Object.entries(
        vendorDates.dateSelectionByMarket,
      ).reduce((prevCount, [_marketId, dateStatuses]) => {
        const requestedCount = Object.entries(dateStatuses).filter(
          ([_dateId, status]) => status === 'REQUESTED',
        ).length;
        return prevCount + requestedCount;
      }, 0);
      if (requestedDatesCount > 0) {
        const proceed = await confirm(
          t('Approve all requested vendor dates?'),
          t(
            `This will approve all {requestedDatesCount} of this vendor's requested dates.`,
            { requestedDatesCount },
          ),
        );
        if (!proceed) {
          return;
        }
      }
    }
    if (newStatus === AppStatus.DECLINED) {
      const proceed = await confirm(
        t('Are you sure?'),
        t(
          'This will mark the vendor as declined and send an email to the vendor (if configured).',
        ),
      );
      if (!proceed) {
        return;
      }
    }
    this.setState({
      statusChangePending: newStatus,
      statusChangeSuccessful: false,
    });
    postJSON(
      `/api/vendors/${vendor.id}/status`,
      { status: newStatus },
      this._statusChangeResponse,
    );
  };

  _statusChangeResponse = (
    error: Error | null | undefined,
    data: {
      [key: string]: any;
    },
  ) => {
    if (!this._isMounted) {
      return;
    }
    let newStatus = this.state.statusChangePending;
    this.setState({ statusChangePending: null });
    if (error) {
      showError(error.message);
      return;
    } else if (!data.success) {
      showError(data.errorMessage);
    } else {
      if (newStatus) {
        let { vendor } = this.props;
        if (vendor) {
          VendorActions.updateVendor(vendor.id, { status: newStatus });
          this.props.refetchAll();
        }
      }
    }

    this._showStatusChangeSuccess();
  };

  _showStatusChangeSuccess = () => {
    clearTimeout(this._statusSuccessIndicatorTimeout);
    this.setState({ statusChangeSuccessful: true }, () => {
      this._statusSuccessIndicatorTimeout = setTimeout(() => {
        this.setState({ statusChangeSuccessful: false });
      }, 2000);
    });
  };

  _toggleTag(tag: Tag) {
    let { vendor, vendorID } = this.props;
    let tagID = tag.id;
    if (!vendor) {
      return;
    }

    let toggleAction: AddOrRemove = 'ADD';

    if (vendor?.tags && vendor?.tags?.includes(tagID)) {
      toggleAction = 'REMOVE';
    }

    let vendorTags: ObjectOf<Array<string>> = {};
    let { tags } = vendor;
    let tagSet: Set<string> = tags ? new Set(tags) : new Set();
    if (toggleAction === 'ADD') {
      tagSet.add(tagID);
    } else {
      tagSet.delete(tagID);
    }
    let tagArray = [...tagSet];
    vendorTags[vendorID] = tagArray;
    VendorActions.updateVendor(vendorID, { tags: tagArray });

    postJSON('/api/vendors/tags', { vendors: vendorTags }, (error, resData) => {
      if (error || resData.error) {
        showError(error ? error.message : resData.error);
      } else {
        showSuccess(t('Tag changed successfully.'));
      }
      this.props.refetchAll();
    });
  }
}

function TagsDisplay(props: { vendor: Vendor; tags: Array<Tag> }) {
  let { vendor, tags } = props;
  let selectedTags = new Set(vendor.tags);
  let tagList = [];
  for (let tag of tags || []) {
    if (selectedTags.has(tag.id)) {
      tagList.push(<TagLabelView key={tag.id} tag={tag} />);
    }
  }
  return <VStack align="flex-start">{tagList}</VStack>;
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
  },
  back: {
    marginBottom: 8,
    alignSelf: 'flex-start',
  },
  leftCard: {
    flex: 3,
    marginRight: 20,
  },
  savedText: {
    fontSize: 12,
  },
  savedIcon: {
    marginRight: 4,
  },
  buttonHeader: {
    marginRight: 25,
  },
  button: {
    paddingHorizontal: 10,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flex: 1,
  },
  leftItem: {
    paddingHorizontal: 30,
    overflowY: 'scroll',
    flex: 1,
  },
  rightCard: {
    flex: 1,
    overflowY: 'scroll',
  },
  signUp: {
    paddingHorizontal: 20,
  },
  marginItem: {
    marginBottom: 20,
  },
  marginRightItem: {
    marginVertical: 20,
  },
  textLabel: {
    color: themeColors.mainNavText,
    fontSize: 16,
    marginBottom: 8,
  },
  tagItem: {
    width: 20,
    height: 20,
    marginRight: 6,
    borderRadius: 4,
  },
});
